import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
  type ReactNode,
} from 'react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  pointerWithin,
  closestCenter,
  type CollisionDetection,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { useOptionalBookmarkSelection } from './BookmarkSelectionContext';
import { useBatchActions } from '../hooks/useBatchActions';
import BookmarkDragOverlay from '../components/Bookmark/BookmarkDragOverlay';
import {
  useReorderBookmark,
  computeReorderedIds,
  neighborsForReorder,
  parseSortableBookmarkId,
  sortableBookmarkId,
  type BookmarkReorderMode,
} from '../services/bookmarkReorder';

export interface BookmarkDragData {
  type: 'bookmark';
  bookmarkIds: string[];
  singleBookmarkId: string;
  bookmarkTitle?: string;
  /** Present when this row participates in list reorder. */
  sortable?: boolean;
}

export interface DropTargetData {
  type: 'folder' | 'tag' | 'home' | 'archive' | 'liked';
  folderId?: string;
  tagName?: string;
}

export type DropIndicatorPosition = 'above' | 'below' | null;

export interface BookmarkListSortConfig {
  bookmarkIds: string[];
  reorderMode: BookmarkReorderMode;
  sortOrder: 'newest' | 'oldest';
}

interface BookmarkDndContextValue {
  isDragging: boolean;
  draggedBookmarkIds: string[];
  activeDropTargetId: string | null;
  /** Sortable id (`bookmark-{id}`) currently being dragged, when list-sort is active. */
  activeSortId: string | null;
  overSortId: string | null;
  justMovedId: string | null;
  setListSortConfig: (config: BookmarkListSortConfig | null) => void;
  getDropIndicatorPosition: (sortableId: string) => DropIndicatorPosition;
}

const BookmarkDndContext = createContext<BookmarkDndContextValue | undefined>(undefined);

export function useBookmarkDnd() {
  const context = useContext(BookmarkDndContext);
  if (!context) {
    throw new Error('useBookmarkDnd must be used within BookmarkDndProvider');
  }
  return context;
}

export function useOptionalBookmarkDnd() {
  return useContext(BookmarkDndContext);
}

const SIDEBAR_DROP_TYPES = new Set(['folder', 'tag', 'home', 'archive', 'liked']);

/**
 * Prefer sidebar drop targets when the pointer is over them; otherwise fall
 * back to closestCenter for in-list sorting among bookmark rows only.
 * Sidebar targets must never win via closestCenter — the dragged row's
 * collision rect is full-width and its center can sit nearer to the nav
 * than the pointer, which caused false Archive/folder highlights.
 */
export const bookmarkCollisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  const sidebarHit = pointerCollisions.find((collision) => {
    const container = args.droppableContainers.find(
      (candidate) => candidate.id === collision.id,
    );
    const data = container?.data.current as DropTargetData | BookmarkDragData | undefined;
    return data != null && SIDEBAR_DROP_TYPES.has((data as DropTargetData).type);
  });
  if (sidebarHit) {
    return [sidebarHit];
  }

  const bookmarkContainers = args.droppableContainers.filter((container) => {
    const data = container.data.current as DropTargetData | BookmarkDragData | undefined;
    return data?.type === 'bookmark';
  });

  return closestCenter({
    ...args,
    droppableContainers: bookmarkContainers,
  });
};

interface BookmarkDndProviderProps {
  children: ReactNode;
}

export function BookmarkDndProvider({ children }: BookmarkDndProviderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [draggedBookmarkIds, setDraggedBookmarkIds] = useState<string[]>([]);
  const [draggedBookmarkTitle, setDraggedBookmarkTitle] = useState<string | undefined>();
  const [activeDropTargetId, setActiveDropTargetId] = useState<string | null>(null);
  const [activeSortId, setActiveSortId] = useState<string | null>(null);
  const [overSortId, setOverSortId] = useState<string | null>(null);
  const [justMovedId, setJustMovedId] = useState<string | null>(null);

  const listSortConfigRef = useRef<BookmarkListSortConfig | null>(null);

  const setListSortConfig = useCallback((config: BookmarkListSortConfig | null) => {
    listSortConfigRef.current = config;
  }, []);

  const selectionContext = useOptionalBookmarkSelection();
  const disableBatchMode = selectionContext?.disableBatchMode;
  const { batchMove, batchMoveHome, batchTag, batchArchive, batchLike } =
    useBatchActions();
  const reorderBookmark = useReorderBookmark();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const resetDragState = useCallback(() => {
    setIsDragging(false);
    setDraggedBookmarkIds([]);
    setDraggedBookmarkTitle(undefined);
    setActiveDropTargetId(null);
    setActiveSortId(null);
    setOverSortId(null);
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const data = active.data.current as BookmarkDragData | undefined;

    if (data?.type === 'bookmark') {
      setIsDragging(true);
      setDraggedBookmarkIds(data.bookmarkIds);
      setDraggedBookmarkTitle(data.bookmarkTitle);
      if (data.sortable) {
        setActiveSortId(String(active.id));
      }
    }
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const over = event.over;
    const targetId = (over?.id as string) ?? null;
    setActiveDropTargetId(targetId);

    const overData = over?.data.current as DropTargetData | BookmarkDragData | undefined;
    if (overData?.type === 'bookmark' && (overData as BookmarkDragData).sortable) {
      setOverSortId(targetId);
    } else {
      setOverSortId(null);
    }
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      const wasSortActive = activeSortId != null;
      resetDragState();

      if (!over) return;

      const dragData = active.data.current as BookmarkDragData | undefined;
      if (!dragData || dragData.type !== 'bookmark') return;

      const dropData = over.data.current as DropTargetData | BookmarkDragData | undefined;
      if (!dropData) return;

      // Sidebar / nav drops — multi-select moves apply
      if (SIDEBAR_DROP_TYPES.has((dropData as DropTargetData).type)) {
        const sidebarDrop = dropData as DropTargetData;
        const bookmarkIds = dragData.bookmarkIds;

        switch (sidebarDrop.type) {
          case 'folder':
            if (sidebarDrop.folderId) {
              batchMove.mutate({ bookmarkIds, folderId: sidebarDrop.folderId });
              disableBatchMode?.();
            }
            break;

          case 'home':
            batchMoveHome.mutate(bookmarkIds);
            disableBatchMode?.();
            break;

          case 'archive':
            batchArchive.mutate(bookmarkIds);
            disableBatchMode?.();
            break;

          case 'liked':
            batchLike.mutate(bookmarkIds);
            disableBatchMode?.();
            break;

          case 'tag':
            if (sidebarDrop.tagName) {
              batchTag.mutate({
                bookmarkIds,
                addTags: [sidebarDrop.tagName],
              });
              disableBatchMode?.();
            }
            break;
        }
        return;
      }

      // In-list reorder — single bookmark only
      if (
        !wasSortActive ||
        !dragData.sortable ||
        dropData.type !== 'bookmark' ||
        !(dropData as BookmarkDragData).sortable
      ) {
        return;
      }

      const config = listSortConfigRef.current;
      if (!config) return;

      const movingId = String(dragData.singleBookmarkId);
      const overId = parseSortableBookmarkId(over.id);
      if (movingId === overId) return;

      const orderedIds = computeReorderedIds(config.bookmarkIds, movingId, overId);
      if (!orderedIds) return;

      const { beforeId, afterId } = neighborsForReorder(
        orderedIds,
        movingId,
        config.sortOrder,
      );
      if (beforeId == null && afterId == null) return;

      setJustMovedId(sortableBookmarkId(movingId));
      window.setTimeout(() => setJustMovedId(null), 200);

      reorderBookmark.mutate({
        bookmarkId: movingId,
        beforeId,
        afterId,
        mode: config.reorderMode,
        orderedBookmarkIds: orderedIds,
      });
    },
    [
      activeSortId,
      resetDragState,
      batchMove,
      batchMoveHome,
      batchTag,
      batchArchive,
      batchLike,
      disableBatchMode,
      reorderBookmark,
    ],
  );

  const handleDragCancel = useCallback(() => {
    resetDragState();
  }, [resetDragState]);

  const getDropIndicatorPosition = useCallback(
    (sortableId: string): DropIndicatorPosition => {
      if (
        !activeSortId ||
        !overSortId ||
        activeSortId === overSortId ||
        sortableId !== overSortId
      ) {
        return null;
      }

      const config = listSortConfigRef.current;
      if (!config) return null;

      const activeId = parseSortableBookmarkId(activeSortId);
      const overId = parseSortableBookmarkId(overSortId);
      const activeIndex = config.bookmarkIds.findIndex((id) => id === activeId);
      const overIndex = config.bookmarkIds.findIndex((id) => id === overId);
      if (activeIndex === -1 || overIndex === -1) return null;

      return activeIndex < overIndex ? 'below' : 'above';
    },
    [activeSortId, overSortId],
  );

  const contextValue = useMemo(
    () => ({
      isDragging,
      draggedBookmarkIds,
      activeDropTargetId,
      activeSortId,
      overSortId,
      justMovedId,
      setListSortConfig,
      getDropIndicatorPosition,
    }),
    [
      isDragging,
      draggedBookmarkIds,
      activeDropTargetId,
      activeSortId,
      overSortId,
      justMovedId,
      setListSortConfig,
      getDropIndicatorPosition,
    ],
  );

  return (
    <BookmarkDndContext.Provider value={contextValue}>
      <DndContext
        sensors={sensors}
        collisionDetection={bookmarkCollisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {children}
        <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]}>
          {isDragging && (
            <BookmarkDragOverlay
              bookmarkIds={draggedBookmarkIds}
              bookmarkTitle={draggedBookmarkTitle}
            />
          )}
        </DragOverlay>
      </DndContext>
    </BookmarkDndContext.Provider>
  );
}
